import {
    streamWebAgentExecute,
    closeWebAgentSession,
    decodeJWT,
    useCreateAiWorkerUsageMutation,
    useUpdateAiWorkerUsageMutation,
    useGetAiworkersPresignedUrlMutation,
    useUploadFileToS3Mutation,
    type RootState,
    type WebAgentExecuteResult,
    type WebAgentSpec,
} from '@circuitry-ai/doc-data';
import { checkType, CtyShowToast, ErrorBoundary, formatKeyForDisplay, getUserInitials, getUserName, parseFieldDefs, validateCommandForm, type UploadedFileType } from '@circuitry-ai/doc-ui';
import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { SharedConverseUI } from './sharedconverseui';
import { toConversePhase, useWebAgentConverse } from './useWebAgentConverse';

function withDurationSuffix(key: string, durationMs?: number): string {
    if (durationMs == null) return key;
    return `${key} (${(durationMs / 1000).toFixed(1)}s)`;
}

function resultWarnings(result: { warnings?: string[] } | null | undefined): string[] {
    return Array.isArray(result?.warnings) ? result.warnings.filter(Boolean) : [];
}

function buildRunSummary(params: {
    agentLabel: string;
    userInput: string;
    stepCount: number;
    succeeded: boolean;
    extractedKeys: string[];
    lastStepLabel?: string;
    errorMessage?: string;
}): string {
    const { agentLabel, userInput, stepCount, succeeded, extractedKeys, lastStepLabel, errorMessage } = params;
    const stepText = `${stepCount} ${stepCount === 1 ? 'step' : 'steps'}`;

    const whatRan = userInput
        ? `The ${agentLabel} was executed for the request "${userInput}".`
        : `The ${agentLabel} was executed using its configured workflow.`;

    if (succeeded) {
        const preview = extractedKeys.slice(0, 6).map((key) => formatKeyForDisplay(key)).join(', ');
        const more = extractedKeys.length > 6 ? `, and ${extractedKeys.length - 6} more` : '';
        const fieldWord = extractedKeys.length === 1 ? 'field' : 'fields';
        const results = extractedKeys.length > 0
            ? `From these it captured ${extractedKeys.length} output ${fieldWord}: ${preview}${more}.`
            : 'It did not capture any structured output fields during this run.';
        return [
            whatRan,
            `It automated ${stepText} and completed the full workflow successfully.`,
            results,
        ].join(' ');
    }

    const lastAction = lastStepLabel ? ` The last action attempted was "${lastStepLabel}".` : '';
    return [
        whatRan,
        `It stopped before completing the workflow, having run ${stepText}.${lastAction}`,
        `The run failed with the error: "${errorMessage ?? 'Run failed'}".`,
        'Review the step-by-step log above to pinpoint where it stopped.',
    ].join(' ');
}

/** Current time as a UTC `YYYY-MM-DDTHH:mm:ss` string (matches the aiworkers/usage API format). */
function toUtcTimestamp(): string {
    const d = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}T${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}:${pad(d.getUTCSeconds())}`;
}

function base64ToFile(base64: string, filename: string, contentType?: string): File | null {
    try {
        const binary = atob(base64);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) bytes[i] = binary.codePointAt(i) ?? 0;
        return new File([bytes], filename, { type: contentType || 'application/octet-stream' });
    } catch {
        return null;
    }
}

function frameToFile(frame: string, filename: string): File | null {
    const commaIdx = frame.indexOf(',');
    const hasHeader = frame.startsWith('data:') && commaIdx >= 0;
    const base64 = hasHeader ? frame.slice(commaIdx + 1) : frame;
    const mime = hasHeader ? /data:(.*?);base64/.exec(frame.slice(0, commaIdx))?.[1] ?? 'image/png' : 'image/png';
    return base64ToFile(base64, filename, mime);
}

export interface WebAgentConverseLayoutProps {
    readonly spec: WebAgentSpec;
    readonly agentName?: string;
    readonly aiworkerId?: string;
    readonly commandJson?: Record<string, any>;
    readonly liveFrames?: boolean;
    readonly customInput?: string;
    readonly showAccordion?: boolean;
    readonly hideTopHeader?: boolean;
    readonly onLoadingChange?: (isLoading: boolean) => void;
    readonly onAgentStreamComplete?: (completed: boolean) => void;
}

const SharedConverseUIWithErrorBoundary = ErrorBoundary(SharedConverseUI);

export function WebAgentConverseLayout({
    spec,
    agentName,
    aiworkerId,
    commandJson,
    liveFrames = false,
    customInput,
    showAccordion = false,
    hideTopHeader = false,
    onLoadingChange,
    onAgentStreamComplete,
}: WebAgentConverseLayoutProps) {
    const { t } = useTranslation();
    const hasCommandFields = Boolean(commandJson && Object.keys(commandJson).length > 0);

    const { details: user, isAuthenticated } = useSelector((state: RootState) => state.auth.user);
    const token = useSelector((state: RootState) => state.auth.token) as string | null;

    const [createAiWorkerUsage] = useCreateAiWorkerUsageMutation();
    const [updateAiWorkerUsage] = useUpdateAiWorkerUsageMutation();
    const [getAiworkersPresignedUrl] = useGetAiworkersPresignedUrlMutation();
    const [uploadFileToS3] = useUploadFileToS3Mutation();

    const [commandFormData, setCommandFormData] = useState<Record<string, string>>({});
    const [commandCustomMessage, setCommandCustomMessage] = useState<string>('');
    const { converseMessages, isStreaming: responseLoading, sendingRef, patchActive, appendPhase, beginTurn, endTurn } = useWebAgentConverse();
    const customInputSentRef = useRef(false);
    const handleSendRef = useRef<(userInput: string, uploadedFiles: UploadedFileType[]) => Promise<void>>(async () => undefined);

    // One browser for this conversation: the first message opens it, each run hands back a handle that
    // resumes it (so a sign-in still holds), and leaving this view frees it. Held in a ref so the
    // unmount below is registered once rather than on every send.
    const sessionHandleRef = useRef('');
    const tokenRef = useRef(token);
    useEffect(() => { tokenRef.current = token; }, [token]);
    useEffect(() => {
        const release = () => {
            const sessionHandle = sessionHandleRef.current;
            sessionHandleRef.current = '';
            if (sessionHandle) void closeWebAgentSession({ sessionHandle, token: tokenRef.current });
        };
        // Leaving the tab open but closing/reloading it never unmounts, so cover that too. A page
        // restored from the back-forward cache simply finds the handle stale and opens a new browser.
        window.addEventListener('pagehide', release);
        return () => {
            window.removeEventListener('pagehide', release);
            release();
        };
    }, []);

    useEffect(() => {
        onLoadingChange?.(responseLoading);
    }, [responseLoading, onLoadingChange]);

    useEffect(() => {
        if (!hasCommandFields || !commandJson || !customInput?.trim()) return;
        const claimPattern = /^(claim|claim.?number|claimnumber)$/i;
        const claimField = parseFieldDefs(commandJson).find((fd) => claimPattern.test(fd.key));
        if (claimField) {
            setCommandFormData((prev) => (prev[claimField.key]?.trim() ? prev : { ...prev, [claimField.key]: customInput.trim() }));
        }
    }, [commandJson, customInput, hasCommandFields]);

    const handleSend = async (_userInput: string, _uploadedFiles: UploadedFileType[]) => {
        if (sendingRef.current) return;

        if (hasCommandFields && commandJson) {
            const result = validateCommandForm(commandJson, commandFormData, commandCustomMessage);
            if (!result.valid) {
                CtyShowToast(checkType.error, t(result.errorKey));
                return;
            }
        }

        const mergedInputs: Record<string, string> = Object.fromEntries(
            Object.entries(commandFormData).map(([key, value]) => [key, String(value ?? '').trim()]),
        );

        const userTurn: any = {
            name: getUserName(isAuthenticated, user),
            textMessage: '',
            viewLogo: false,
            initials: getUserInitials(user?.nickname),
            commandFormData: { ...commandFormData },
            commandCustomMessage,
        };

        const signal = beginTurn(userTurn, { name: agentName ?? t('Web Agent'), textMessage: '', phases: [], viewLogo: true });
        onAgentStreamComplete?.(false);

        const usageContext = buildUsageContext(mergedInputs, _userInput);
        const startTracking = () => startUsageTracking(usageContext);
        const stepFramesById: Record<string, string[]> = {};
        let stepIndex = 0;

        let finalResult: WebAgentExecuteResult | null = null;
        let errorMessage: string | undefined;
        await streamWebAgentExecute({
            spec, inputs: mergedInputs, token, keepSession: true, sessionHandle: sessionHandleRef.current, captureFrames: liveFrames, signal,
            onEvent: (event) => {
                if (event.type === 'step') {
                    startTracking();
                    const stepId = event.step.id ?? `step-${stepIndex + 1}`;
                    stepIndex += 1;
                    if (event.screenshot) {
                        const frames = stepFramesById[stepId] ?? [];
                        frames.push(event.screenshot);
                        stepFramesById[stepId] = frames;
                    }
                    appendPhase(toConversePhase(event, t('Step')));
                } else if (event.type === 'done') {
                    startTracking();
                    // Empty when the runner closed the browser (a run that never got signed in), so the
                    // next message starts a fresh one and runs the sign-in again.
                    sessionHandleRef.current = event.result.sessionHandle ?? '';
                    finalResult = event.result;
                    // Attachments already have s3_keys injected by the runner (presign + S3 PUT done server-side).
                    finalizeRun(event.result);
                } else if (event.type === 'error') {
                    errorMessage = event.message;
                    patchActive((msg) => ({ ...msg, textMessage: event.message }));
                }
            },
        });

        await completeUsageTracking(
            usageContext,
            finalResult,
            errorMessage,
            stepFramesById,
        );
        endTurn();
        onAgentStreamComplete?.(true);
    };
    handleSendRef.current = handleSend;

    // Same as the Dify converse path: when there is no command form, send the preset input once the
    // workflow spec is available (PAL / extension rerun with a claim number).
    useEffect(() => {
        if (hasCommandFields) return;
        const input = customInput?.trim();
        if (!input || customInputSentRef.current) return;
        if (!spec.startUrl && !(spec.steps?.length)) return;
        customInputSentRef.current = true;
        void handleSendRef.current(input, []);
    }, [customInput, hasCommandFields, spec.startUrl, spec.steps]);

    const buildUsageContext = (inputs: Record<string, string>, rawInput: string) => {
        if (!aiworkerId || !token) return null;

        const decoded = decodeJWT(token);
        const tenantId = String((decoded as any)?.tenant_id ?? '');
        const executedBy = String(decoded?.sub ?? '');
        const usageId = crypto.randomUUID();

        const tags = Object.entries(inputs).map(([key, value]) => ({ key, value }));
        const values = Object.values(inputs).map((v) => String(v ?? '').trim()).filter(Boolean);
        const userInput = values.length > 0 ? values.join(' ') : String(rawInput ?? commandCustomMessage ?? '').trim();

        return { usageId, tenantId, executedBy, tags, inputData: { user_input: userInput }, started: false, createPromise: Promise.resolve() as Promise<unknown> };
    };

    type UsageContext = NonNullable<ReturnType<typeof buildUsageContext>>;

    /** Fire the POST that creates the usage row (status `in_progress`). Runs at most once per run. */
    const startUsageTracking = (context: UsageContext | null) => {
        if (!context || context.started) return;
        context.started = true;
        context.createPromise = createAiWorkerUsage({
            aiworker_id: aiworkerId as string,
            aiworker_type: 'action_logs',
            used_at: toUtcTimestamp(),
            status: 'in_progress',
            tenant_id: context.tenantId,
            executed_by: context.executedBy,
            message_id: context.usageId,
            aiworker_usage_id: context.usageId,
            input_data: context.inputData,
            tags: context.tags,
        }).unwrap().catch(() => undefined);
    };

    const uploadStepFrames = async (stepFramesById: Record<string, string[]>): Promise<Record<string, string[]>> => {
        const flat = Object.entries(stepFramesById)
            .flatMap(([stepId, frames]) => frames.map((frame, i) => ({ stepId, frame, filename: `${stepId}-${i + 1}.png` })));
        if (!liveFrames || flat.length === 0) return {};

        try {
            const presigned: Array<{ presigned_url?: string; s3_key?: string }> =
                await getAiworkersPresignedUrl(flat.map((f) => ({ filename: f.filename, source_type: 'action' }))).unwrap();
            if (!Array.isArray(presigned) || presigned.length === 0) return {};

            const keysByStep: Record<string, string[]> = {};
            flat.forEach((f, i) => {
                const { presigned_url: presignedUrl, s3_key: s3Key } = presigned[i] ?? {};
                const file = frameToFile(f.frame, f.filename);
                if (!presignedUrl || !s3Key || !file) return;
                // Fire-and-forget: don't block the PATCH on the S3 upload completing.
                uploadFileToS3({ file, presignedUrl, CtyShowToast: () => undefined }).unwrap().catch(() => undefined);
                const stepKeys = keysByStep[f.stepId] ?? [];
                stepKeys.push(s3Key);
                keysByStep[f.stepId] = stepKeys;
            });
            return keysByStep;
        } catch {
            return {};
        }
    };

    /** PATCH the usage row created in {@link startUsageTracking} with the final execution result. */
    const completeUsageTracking = async (
        context: UsageContext | null,
        result: WebAgentExecuteResult | null,
        errorMessage?: string,
        stepFramesById: Record<string, string[]> = {},
    ) => {
        if (!context?.started) return;
        await context.createPromise;

        const succeeded = result?.status === 'success';
        const extracted: Record<string, unknown> = result?.extracted ?? {};

        const attachmentKeysByStep = await uploadStepFrames(stepFramesById);
        const stepLog = Array.isArray(result?.stepLog) ? result?.stepLog : [];
        const logMessages = Array.isArray(result?.logs) ? result?.logs : [];
        const warnings = resultWarnings(result);
        const totalSeconds = stepLog.reduce((sum, entry) => sum + (entry.durationMs ?? 0), 0) / 1000;
        const logs: Array<Record<string, unknown>> = [];
        stepLog.forEach((entry, i) => {
            const stepId = entry.id ?? `step-${i + 1}`;
            const logKey = withDurationSuffix(stepId, entry.durationMs);
            logs.push({ [logKey]: String(entry.message ?? logMessages[i] ?? entry.action ?? '') });
            const keys = attachmentKeysByStep[stepId];
            if (keys?.length) logs.push({ '@attachment': { s3_keys: keys } });
        });
        for (const warning of warnings) {
            logs.push({ [t('Warning')]: warning });
        }
        logs.push({ [t('Total time taken')]: `${totalSeconds.toFixed(1)}s` });

        const lastStep = stepLog[stepLog.length - 1];
        const lastStepLabel = lastStep ? String(lastStep.message ?? lastStep.action ?? '') : '';
        const summary = buildRunSummary({
            agentLabel: agentName ?? t('Web Agent'),
            userInput: String(context.inputData.user_input ?? ''),
            stepCount: stepLog.length,
            succeeded,
            extractedKeys: Object.keys(extracted),
            lastStepLabel,
            errorMessage: result?.error?.message ?? errorMessage,
        });

        const body: Record<string, unknown> = {
            tenant_id: context.tenantId,
            agent_score: 'NA',
            status: succeeded ? 'succeeded' : 'failed',
            executed_by: context.executedBy,
            message_id: context.usageId,
            input_data: context.inputData,
            output_data: {
                requests: [{ actions: { logs, extracted } }],
                summary,
            },
            tags: context.tags,
        };
        if (!succeeded) body.error_message = result?.error?.message ?? errorMessage ?? t('Run failed');

        updateAiWorkerUsage({ aiworker_usage_id: context.usageId, body }).unwrap().catch(() => undefined);
    };

    const finalizeRun = (result: WebAgentExecuteResult) => {
        const stepLog = Array.isArray(result.stepLog) ? result.stepLog : [];
        const logs = Array.isArray(result.logs) ? result.logs : [];
        const warnings = resultWarnings(result);
        const totalSeconds = stepLog.reduce((sum, entry) => sum + (entry.durationMs ?? 0), 0) / 1000;
        const extracted = result.extracted ?? {};
        const hasExtracted = Object.keys(extracted).length > 0;

        let textMessage: string;
        if (result.status === 'error') textMessage = result.error?.message || t('Run failed');
        else if (hasExtracted) textMessage = t('Completed');
        else textMessage = logs[logs.length - 1] || t('Completed');

        if (result.status === 'success' && warnings.length > 0) {
            textMessage = `${textMessage}\n\n${warnings.join('\n')}`;
        }

        patchActive((msg) => ({
            ...msg,
            textMessage,
            total_time_taken_seconds: totalSeconds,
            ...(hasExtracted ? { structuredOutput: extracted } : {}),
        }));
    };

    return (
        // The titled section used by the widget and extension brings its own spacing,
        // so the page padding is dropped there to keep the conversation full width.
        <div className="flex h-full flex-col">
            <SharedConverseUIWithErrorBoundary
                name={agentName ?? t('Web Agent')}
                responseLoading={responseLoading}
                handleSend={handleSend}
                converseMessages={converseMessages}
                aiworker_id={aiworkerId ?? ''}
                commandJson={hasCommandFields ? commandJson : {}}
                commandFormData={commandFormData}
                commandCustomMessage={commandCustomMessage}
                onCommandFormDataChange={setCommandFormData}
                onCommandCustomMessageChange={setCommandCustomMessage}
                commandSteps={spec.steps}
                showAccordion={showAccordion}
                hideTopHeader={hideTopHeader}
                showStepDuration
            />
        </div>
    );
}
